"use client";

import { z } from "zod";

import { isEcommercePreviewReadOnly } from "@/config/preview-mode";
import type { CatalogEvent } from "@/domain/analytics/catalog-event";

const sessionStorageKey = "ib:ecommerce:session:v1";
const sessionCookie = "ib_catalog_session";

export function getCatalogSessionId(): string {
  const savedSessionId = readSavedSessionId();

  if (savedSessionId && z.string().uuid().safeParse(savedSessionId).success) {
    writeSessionCookie(savedSessionId);
    return savedSessionId;
  }

  const sessionId = createSessionId();
  writeSavedSessionId(sessionId);
  writeSessionCookie(sessionId);

  return sessionId;
}

function writeSessionCookie(sessionId: string): void {
  try {
    const secure = window.location?.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${sessionCookie}=${sessionId}; Path=/; Max-Age=86400; SameSite=Lax${secure}`;
  } catch {
    // Session attribution must not block catalog browsing.
  }
}

export function trackCatalogEvent(
  event: Omit<CatalogEvent, "sessionId">,
  options: Readonly<{ keepalive?: boolean }> = {},
): void {
  if (typeof window === "undefined" || isEcommercePreviewReadOnly()) {
    return;
  }

  const payload: CatalogEvent = {
    ...event,
    sessionId: getCatalogSessionId(),
  };

  window
    .fetch("/api/analytics/events", {
      body: JSON.stringify(payload),
      headers: { "Content-Type": "application/json" },
      keepalive: options.keepalive,
      method: "POST",
    })
    .catch(() => undefined);
}

export function createCatalogSearchSubmittedEvent(
  query: string,
): Omit<CatalogEvent, "sessionId"> | null {
  const normalizedQuery = query.trim();

  if (!normalizedQuery) {
    return null;
  }

  return {
    eventName: "catalog_search_submitted",
    query: normalizedQuery,
  };
}

function readSavedSessionId(): string | null {
  try {
    return window.localStorage.getItem(sessionStorageKey);
  } catch {
    return null;
  }
}

function writeSavedSessionId(sessionId: string): void {
  try {
    window.localStorage.setItem(sessionStorageKey, sessionId);
  } catch {
    // Analytics must never block catalog browsing when storage is unavailable.
  }
}

function createSessionId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (character) => {
      const randomByte = crypto.getRandomValues(new Uint8Array(1))[0] ?? 0;
      return (
        Number(character) ^
        (randomByte & (15 >> (Number(character) / 4)))
      ).toString(16);
    });
  }

  return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (character) => {
    const randomByte = Math.floor(Math.random() * 256);
    return (
      Number(character) ^
      (randomByte & (15 >> (Number(character) / 4)))
    ).toString(16);
  });
}
